import type {
  createPlayer as CreatePlayerV2,
  LoadRequest,
  PlayerInterface as PlayerInterfaceV2,
  RequestHeaderCallback,
} from '@foundation-player/api';
import { EncryptionType, ManifestType } from '@foundation-player/runtime';
import type { LoaderConfig, ManifestConfig, MediaConfig, PlayerInterface, StUSConfig } from '@foundation-player/types';
import { loadScript } from './load-script';

/* eslint-disable @typescript-eslint/no-explicit-any */

type Window = {
  foundation: {
    api: {
      createPlayer: typeof CreatePlayerV2;
    };
  };
};

export async function createPlayerV2(
  bundleTarget: 'chrome108' | 'chrome63' | 'chrome47',
  version: string,
  mountPoint: HTMLElement,
  loaderConfig: LoaderConfig,
  uiConfig?: any,
): Promise<PlayerInterface> {
  const url = `https://cdn.player.foundation/api/${version}/bundle.${bundleTarget}.js`;
  await loadScript(url);

  const hasVideoElement = Boolean(mountPoint.getElementsByTagName('video')[0]);
  if (!hasVideoElement) {
    // eslint-disable-next-line no-console
    console.warn('No video element found in mount point. Generating one.');
    const videoElement = document.createElement('video');
    mountPoint.appendChild(videoElement);
  }

  const foundationGlobal = (window as unknown as Window).foundation;
  let playerV2: PlayerInterfaceV2;
  let nonLinearElement: HTMLElement;
  let requestHeaderCallback: RequestHeaderCallback;

  const methods: { [key in keyof PlayerInterface]: 'proxy' | 'stub' | 'custom' } = {
    dispatchEvent: 'stub',
    addEventListener: 'stub',
    listenOnce: 'stub',
    removeEventListener: 'stub',
    isDestroyable: 'stub',
    isDestroyed: 'stub',
    loadMedia: 'custom',
    getPlayerConfig: 'stub',
    getMediaConfig: 'stub',
    pause: 'proxy',
    mute: 'proxy',
    unmute: 'proxy',
    isMuted: 'stub',
    isLive: 'stub',
    play: 'proxy',
    destroy: 'proxy',
    getVersion: 'proxy',
    getCurrentTime: 'stub',
    setVolume: 'proxy',
    getVolume: 'stub',
    seek: 'proxy',
    isPlaying: 'stub',
    getDuration: 'stub',
    canAutoplay: 'stub',
    getPlaybackSpeed: 'stub',
    setPlaybackSpeed: 'proxy',
    getMediaURL: 'stub',
    getPlayerElement: 'stub',
    getVideoElement: 'stub',
    getAvailableAudioTracks: 'stub',
    getActiveAudioTrack: 'stub',
    setAudioTrack: 'proxy',
    getAvailableVideoTracks: 'stub',
    getActiveVideoTrack: 'stub',
    getThumbnail: 'proxy',
    getCurrentBitrate: 'stub',
    setMaxBitrate: 'stub',
    getMaxBitrate: 'stub',
    enableTextTrack: 'proxy',
    disableTextTrack: 'proxy',
    getTextTracks: 'stub',
    getActiveTextTrack: 'stub',
    getActiveSubtitles: 'stub',
    enableSubtitle: 'stub',
    disableSubtitle: 'stub',
    getSubtitles: 'stub',
    getConsent: 'stub',
    setConsentCallback: 'stub',
    getRequestHeader: 'stub',
    setRequestHeaderCallback: 'custom',
    getId: 'proxy',
    getAdvertisingAdapter: 'custom',
    getPlugin: 'stub',
    setMetadata: 'stub',
    timeShift: 'stub',
    seekToLiveEdge: 'stub',
    getDVRWindowInSeconds: 'stub',
    distanceToLiveEdgeInSeconds: 'stub',
    isYospaceStream: 'stub',
  };

  const playerProxy: any = {};
  const methodEntries = Object.entries(methods);
  for (const [method, type] of methodEntries) {
    if (type === 'proxy') {
      playerProxy[method] = (...args: any[]) => (playerV2 as any)[method](...args);
      continue;
    }

    if (type === 'stub') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      playerProxy[method] = () => {};
      continue;
    }

    switch (method) {
      case 'getAdvertisingAdapter':
        playerProxy[method] = () => {
          return {
            setNonLinearElement: (element: HTMLElement) => {
              nonLinearElement = element;
            },
          };
        };
        break;
      case 'setRequestHeaderCallback':
        playerProxy[method] = (callback: RequestHeaderCallback) => {
          requestHeaderCallback = callback;
        };
        break;
      case 'loadMedia': {
        const manifestType = (type: ManifestType) => {
          if (type === ManifestType.Progressive) {
            return 'progressive';
          }

          return type.includes('dash') ? 'dash' : 'hls';
        };

        const keySystem = (type: EncryptionType) => {
          switch (type) {
            case EncryptionType.FairPlay:
              return 'fairplay';
            case EncryptionType.Widevine:
              return 'widevine';
            case EncryptionType.PlayReady:
              return 'playready';
            case 'none':
              throw new Error('No key system provided');
          }
        };

        const clientIdentifier = `html5/${loaderConfig.client}/${(loaderConfig as any).scope}`;

        playerProxy[method] = async (mediaConfig: MediaConfig<ManifestConfig | StUSConfig>) => {
          const loadRequest: LoadRequest = {
            muted: mediaConfig.muted || loaderConfig.playerConfig?.muted,
            autoplay: mediaConfig.autoplay || loaderConfig.playerConfig?.autoplay,
            playbackRate: mediaConfig.playbackSpeed || loaderConfig.playerConfig?.playbackSpeed,
            startTimeInSeconds: mediaConfig.startTime,
            remoteConfig: loaderConfig.client
              ? {
                  environment: loaderConfig.environment,
                  clientIdentifier,
                }
              : undefined,
            contentId: mediaConfig.id as LoadRequest['contentId'],
            contentType: mediaConfig.type as LoadRequest['contentType'],
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            manifests: ((mediaConfig as MediaConfig).manifests || []).map((manifest) => ({
              type: manifestType(manifest.type),
              sources: manifest.sources.map((source) => ({
                url: source.url,
                isYospace: source.isYospace,
                npawProfileName: source.npawProfileName,
              })),
              licenseServers: (manifest.licenseServers || []).map((licenseServer) => ({
                type: keySystem(licenseServer.encryptionType),
                url: licenseServer.address,
                headers: licenseServer.headers,
                certificate: licenseServer.certificate,
              })),
            })),
            plugins: {
              'foundation.plugin.ui': uiConfig,
            },
          };

          playerV2 = await foundationGlobal.api.createPlayer({
            loadRequest,
            nonLinearElement,
            getRequestHeader: requestHeaderCallback,
            mountPoint,
          });
        };
        break;
      }
    }
  }

  return playerProxy;
}
